import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { niceTicks, scaler } from '../../analytics/chartScale';
import { compact, exact, money } from '../../analytics/analyticsFormat';
import { seriesColor } from '../../analytics/charts/palette';
import { type ChartData, axisPlan, tickAt } from '../series';

/**
 * The dashboard's charts.
 *
 * A second chart kit rather than the analytics one, for one structural reason:
 * every chart there draws ONE series (with an optional faded comparison behind
 * it), and every card here may draw several — a metric carries a list of
 * subcategories and "New jobs against Done jobs" is the whole point of putting
 * them on one card. Bending a single-series line chart into a multi-series one
 * would have been a rewrite of a tested component used by a screen that does
 * not need it.
 *
 * What is NOT duplicated is anything that had to be decided once: the scales
 * come from `analytics/chartScale`, the colours from `analytics/charts/palette`
 * — slots assigned in fixed order, never cycled — and the number formatting
 * from `analytics/analyticsFormat`, where the awkward cases (growth from zero
 * is "new", not infinity) are already pinned by tests.
 */

interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

function useTooltip() {
  const [tip, setTip] = useState<TipState | null>(null);
  const show = useCallback((event: { clientX: number; clientY: number }, content: ReactNode) => {
    setTip({ x: event.clientX, y: event.clientY, content });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  const node = tip ? (
    <div
      className="db-tip"
      role="status"
      style={{
        left: Math.min(tip.x + 14, window.innerWidth - 260),
        top: Math.max(8, tip.y - 12),
      }}
    >
      {tip.content}
    </div>
  ) : null;
  return { show, hide, node };
}

/**
 * The drawing width in real pixels.
 *
 * An SVG with a fixed viewBox and `width: 100%` does NOT stretch — the default
 * `preserveAspectRatio` letterboxes it. `preserveAspectRatio="none"` stretches
 * instead and shears the strokes and the axis text. Measuring and drawing 1:1
 * is the only option that does neither, at the cost of one ResizeObserver.
 */
function useMeasuredWidth(fallback: number) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = hostRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [hostRef, width] as const;
}

/*
 * Not exported, deliberately: a file that exports both components and values
 * breaks Vite's fast refresh for the whole file — the same reason the analytics
 * palette sits in its own module rather than beside the charts that use it.
 */

/** Counts get a compact integer; money gets the currency, because "1.2k" of what is not an answer. */
const figureFormatter = (kind: ChartData['kind']) =>
  kind === 'currency' ? (value: number) => money(value) : compact;

/**
 * Enough gutter for the widest tick the axis will print. A count never runs past
 * "450k"; money runs as wide as the company's sign makes it — "$6,000" and
 * "COL$40,000,000" are both somebody's ordinary axis — so a money gutter is
 * measured off the printed ticks, and is never narrower than the 62px "$6,000"
 * has always had. About 6px a character at the axis' 10px type.
 */
const axisGutter = (kind: ChartData['kind'], ticks: string[]) => {
  if (kind !== 'currency') return 44;
  const widest = Math.max(0, ...ticks.map((tick) => tick.length));
  return Math.max(62, Math.ceil(widest * 6) + 14);
};

const exactFormatter = (kind: ChartData['kind']) =>
  kind === 'currency' ? (value: number) => money(value) : exact;

interface ChartProps {
  data: ChartData;
  height?: number;
  /**
   * The bucket key to pick out, or null.
   *
   * What a widened window needs: the chart draws a week so there is a shape to
   * read, and this is the day the operator actually asked for. A band behind
   * it rather than a different colour on the bars — the colours already mean
   * "which series", and a second meaning on them is how a legend starts lying.
   */
  highlight?: string | null;
  /** Why the chart is wider than the window, said once under the readout. */
  focusNote?: string | null;
  /**
   * Read by TAPPING rather than hovering: the phone screen's charts.
   *
   * A tooltip that follows the pointer is drawn exactly where a finger is, so
   * on touch a tap PINS its bucket instead — picked out like the widened day,
   * with its figures in the readout line under the chart — and a second tap
   * on it lets go.
   */
  touch?: boolean;
}

/** Said under a phone chart until something is tapped, so the reading can be found. */
function TapHint({ line }: { line?: boolean }) {
  return <p className="db-readout hint">{line ? 'Tap the chart to read a day' : 'Tap a bar to read it'}</p>;
}

/**
 * The highlighted bucket, in words.
 *
 * The on-mark value is skipped whenever the bar is narrower than the text — a
 * clipped label is worse than none — and on a three-series card at card width
 * that is always. So the reading it was there to give is given here instead:
 * the bucket's own label and every series' value at it, which fits at any width
 * and needs no colour matching.
 */
function FocusReadout({
  data,
  index,
  note,
}: {
  data: ChartData;
  index: number;
  note?: string | null;
}) {
  if (index < 0 || index >= data.buckets.length) return null;
  const format = exactFormatter(data.kind);
  const bucket = data.buckets[index];
  return (
    <p className="db-readout">
      <strong>{bucket.label}</strong>
      {data.series.map((series, seriesIndex) => (
        <span key={series.code}>
          <i className="db-swatch" style={{ background: seriesColor(seriesIndex) }} />
          {data.series.length > 1 ? `${series.label} ` : ''}
          {format(bucket.values[seriesIndex])}
        </span>
      ))}
      {note ? <span className="db-readout-note">{note}</span> : null}
    </p>
  );
}

function Legend({ data }: { data: ChartData }) {
  // One series needs no legend: the card's own title already names it, and a
  // swatch under it says nothing the colour has not already said.
  if (data.series.length < 2) return null;
  return (
    <div className="db-legend">
      {data.series.map((series, index) => (
        <span key={series.code}>
          <i className="db-swatch" style={{ background: seriesColor(index) }} />
          {series.label}
        </span>
      ))}
    </div>
  );
}

function Empty() {
  return <div className="db-empty">Nothing in this window.</div>;
}

/* ---- columns -------------------------------------------------------------- */

/**
 * Grouped columns — the default shape for a metric over time.
 *
 * Grouped rather than stacked, deliberately. A stack answers "what did these
 * add up to", and these series almost never do add up to anything: New Jobs
 * and Cancelled Jobs on one card are two counts of overlapping sets, and
 * stacking them draws a total nobody asked for and that is not true.
 */
export function ColumnChart({ data, height = 220, highlight = null, focusNote = null, touch = false }: ChartProps) {
  const ref = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const { show, hide, node } = useTooltip();
  const [hostRef, width] = useMeasuredWidth(640);

  const count = data.buckets.length;
  const highlightIndex = highlight ? data.buckets.findIndex((bucket) => bucket.key === highlight) : -1;
  const max = Math.max(1, ...data.buckets.flatMap((bucket) => bucket.values));
  const ticks = niceTicks(max);
  const format = figureFormatter(data.kind);
  const tipFormat = exactFormatter(data.kind);
  // Money runs to "CA$6,000" where a count runs to "450", so the axis gutter is
  // set by what will be printed in it. The axis BAND below is measured before
  // the plot is, because turning the ticks to fit every bucket is what decides
  // how much room they need underneath.
  const gutter = axisGutter(data.kind, ticks.map(format));
  const axis = axisPlan(
    data.buckets.map((bucket) => bucket.label),
    Math.max(1, (width - gutter - 10) / Math.max(count, 1)),
  );
  const plot = { width, height, padTop: 14, padRight: 10, padBottom: axis.pad, padLeft: gutter };
  const scale = scaler(plot, Math.max(count, 1), max);
  const baseline = plot.padTop + scale.innerH;

  // `band`/`bandWidth` rather than dividing the width here: columns sit IN a
  // slot where a line's points sit ON one, and that is exactly the difference
  // those two helpers exist to keep straight.
  const slot = scale.bandWidth;
  // Keep a gutter between groups, and never draw a bar under a pixel wide —
  // below that the chart is a grey wash and the hover is the only way to read it.
  const groupWidth = Math.max(1, slot * 0.72);
  const barWidth = Math.max(1, groupWidth / Math.max(data.series.length, 1));
  const labelAt = tickAt(count, axis, highlightIndex);
  // A tapped bucket takes over the reading from the widened day: its band, its
  // value over the bars, its tick — and the other buckets step back, because a
  // finger covers the bar it chose and the band alone is too quiet to find.
  const pinnedAt = touch ? pinned : null;
  const valueAt = pinnedAt ?? highlightIndex;

  const indexAt = (event: { clientX: number }): number | null => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || !count) return null;
    const px = ((event.clientX - rect.left) / rect.width) * width;
    return Math.max(0, Math.min(count - 1, Math.floor((px - plot.padLeft) / Math.max(slot, 1))));
  };

  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const index = indexAt(event);
    if (index === null) return;
    setHover(index);
    const bucket = data.buckets[index];
    show(
      event,
      <>
        <b>{bucket.label}</b>
        {data.series.map((series, seriesIndex) => (
          <div className="db-tip-row" key={series.code}>
            <span className="db-swatch" style={{ background: seriesColor(seriesIndex) }} />
            {series.label} <b>{tipFormat(bucket.values[seriesIndex])}</b>
          </div>
        ))}
      </>,
    );
  };

  if (!count) return <Empty />;

  return (
    <div ref={hostRef}>
      <svg
        ref={ref}
        className="db-chart"
        viewBox={`0 0 ${width} ${height}`}
        style={{ height }}
        role="img"
        aria-label={data.series.map((series) => series.label).join(', ')}
        onPointerMove={touch ? undefined : onMove}
        onPointerDown={
          touch
            ? (event) => {
                const index = indexAt(event);
                if (index !== null) setPinned((current) => (current === index ? null : index));
              }
            : undefined
        }
        onPointerLeave={
          touch
            ? undefined
            : () => {
                setHover(null);
                hide();
              }
        }
      >
        <title>{data.series.map((series) => series.label).join(', ')}</title>
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className="db-grid-line"
              x1={plot.padLeft}
              x2={width - plot.padRight}
              y1={scale.y(tick)}
              y2={scale.y(tick)}
            />
            <text x={plot.padLeft - 7} y={scale.y(tick) + 3.5} textAnchor="end">
              {format(tick)}
            </text>
          </g>
        ))}
        {/* Under the hover band, so pointing at the highlighted bucket still
            reads as pointing rather than as nothing changing. */}
        {highlightIndex >= 0 ? (
          <rect
            className="db-focus-band"
            x={scale.band(highlightIndex)}
            y={plot.padTop}
            width={slot}
            height={scale.innerH}
          />
        ) : null}
        {(touch ? pinned : hover) !== null ? (
          <rect
            className={touch ? 'db-hover-band pinned' : 'db-hover-band'}
            x={scale.band((touch ? pinned : hover) as number)}
            y={plot.padTop}
            width={slot}
            height={scale.innerH}
          />
        ) : null}
        {data.buckets.map((bucket, index) => {
          const groupLeft = scale.band(index) + (slot - groupWidth) / 2;
          return (
            <g key={bucket.key} className={pinnedAt !== null && pinnedAt !== index ? 'db-dim' : undefined}>
              {bucket.values.map((value, seriesIndex) => {
                const y = scale.y(value);
                return (
                  <rect
                    key={data.series[seriesIndex].code}
                    className="db-bar"
                    x={groupLeft + seriesIndex * barWidth}
                    y={y}
                    width={Math.max(1, barWidth - (barWidth > 3 ? 1 : 0))}
                    // A zero would otherwise be a 0-height rect, which some
                    // engines drop entirely and others draw as a hairline.
                    height={Math.max(0, baseline - y)}
                    fill={seriesColor(seriesIndex)}
                  />
                );
              })}
            </g>
          );
        })}
        {/* The value of the bucket the chart was widened to show — the one
            direct label the chart carries, because it is the one the reader
            asked for. Skipped where the bar is narrower than the text, which
            is a clipped label rather than a read one. */}
        {valueAt >= 0 && barWidth >= 26
          ? data.buckets[valueAt].values.map((value, seriesIndex) => (
              <text
                key={data.series[seriesIndex].code}
                className="db-bar-value"
                x={scale.band(valueAt) + (slot - groupWidth) / 2 + seriesIndex * barWidth + barWidth / 2}
                y={scale.y(value) - 4}
                textAnchor="middle"
              >
                {format(value)}
              </text>
            ))
          : null}
        {labelAt.map((index) => (
          <text
            key={data.buckets[index].key}
            className={index === highlightIndex || index === pinnedAt ? 'db-tick on' : 'db-tick'}
            x={scale.band(index) + slot / 2}
            y={plot.padTop + scale.innerH + (axis.angle === 0 ? 16 : 12)}
            textAnchor={axis.angle === 0 ? 'middle' : 'end'}
            transform={
              axis.angle === 0
                ? undefined
                : `rotate(${axis.angle} ${scale.band(index) + slot / 2} ${
                    plot.padTop + scale.innerH + 12
                  })`
            }
          >
            {data.buckets[index].label}
          </text>
        ))}
      </svg>
      <FocusReadout data={data} index={touch && pinned !== null ? pinned : highlightIndex} note={focusNote} />
      {touch && pinned === null && highlightIndex < 0 ? <TapHint /> : null}
      <Legend data={data} />
      {touch ? null : node}
    </div>
  );
}

/* ---- lines ---------------------------------------------------------------- */

/** Multi-series line, for a metric saved as a trend rather than as columns. */
export function TrendChart({ data, height = 220, highlight = null, focusNote = null, touch = false }: ChartProps) {
  const ref = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const { show, hide, node } = useTooltip();
  const [hostRef, width] = useMeasuredWidth(640);

  const count = data.buckets.length;
  const highlightIndex = highlight ? data.buckets.findIndex((bucket) => bucket.key === highlight) : -1;
  const max = Math.max(1, ...data.buckets.flatMap((bucket) => bucket.values));
  const ticks = niceTicks(max);
  const format = figureFormatter(data.kind);
  const tipFormat = exactFormatter(data.kind);
  // Money runs to "CA$6,000" where a count runs to "450", so the axis gutter
  // is set by what will be printed in it rather than by one number for both.
  const gutter = axisGutter(data.kind, ticks.map(format));
  const axis = axisPlan(
    data.buckets.map((bucket) => bucket.label),
    Math.max(1, (width - gutter - 10) / Math.max(count, 1)),
  );
  const plot = { width, height, padTop: 14, padRight: 10, padBottom: axis.pad, padLeft: gutter };
  const scale = scaler(plot, Math.max(count, 1), max);
  const labelAt = tickAt(count, axis, highlightIndex);
  // A tapped point takes over the reading from the widened day, as on columns.
  const pinnedAt = touch ? pinned : null;
  const valueAt = pinnedAt ?? highlightIndex;

  const pathFor = (seriesIndex: number) =>
    data.buckets
      .map((bucket, index) => `${index === 0 ? 'M' : 'L'}${scale.x(index)} ${scale.y(bucket.values[seriesIndex])}`)
      .join(' ');

  const indexAt = (event: { clientX: number }): number | null => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || !count) return null;
    const px = ((event.clientX - rect.left) / rect.width) * width;
    const step = count > 1 ? scale.innerW / (count - 1) : scale.innerW;
    return Math.max(0, Math.min(count - 1, Math.round((px - plot.padLeft) / step)));
  };

  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const index = indexAt(event);
    if (index === null) return;
    setHover(index);
    const bucket = data.buckets[index];
    show(
      event,
      <>
        <b>{bucket.label}</b>
        {data.series.map((series, seriesIndex) => (
          <div className="db-tip-row" key={series.code}>
            <span className="db-swatch" style={{ background: seriesColor(seriesIndex) }} />
            {series.label} <b>{tipFormat(bucket.values[seriesIndex])}</b>
          </div>
        ))}
      </>,
    );
  };

  if (!count) return <Empty />;

  return (
    <div ref={hostRef}>
      <svg
        ref={ref}
        className="db-chart"
        viewBox={`0 0 ${width} ${height}`}
        style={{ height }}
        role="img"
        aria-label={data.series.map((series) => series.label).join(', ')}
        onPointerMove={touch ? undefined : onMove}
        onPointerDown={
          touch
            ? (event) => {
                const index = indexAt(event);
                if (index !== null) setPinned((current) => (current === index ? null : index));
              }
            : undefined
        }
        onPointerLeave={
          touch
            ? undefined
            : () => {
                setHover(null);
                hide();
              }
        }
      >
        <title>{data.series.map((series) => series.label).join(', ')}</title>
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className="db-grid-line"
              x1={plot.padLeft}
              x2={width - plot.padRight}
              y1={scale.y(tick)}
              y2={scale.y(tick)}
            />
            <text x={plot.padLeft - 7} y={scale.y(tick) + 3.5} textAnchor="end">
              {format(tick)}
            </text>
          </g>
        ))}
        {/* A line rather than a band on a trend: the shape between the points
            is the reading, and a band laid over it obscures the one bucket it
            is trying to pick out. */}
        {highlightIndex >= 0 ? (
          <line
            className="db-focus-line"
            x1={scale.x(highlightIndex)}
            x2={scale.x(highlightIndex)}
            y1={plot.padTop}
            y2={plot.padTop + scale.innerH}
          />
        ) : null}
        {(touch ? pinned : hover) !== null ? (
          <line
            className="db-crosshair"
            x1={scale.x((touch ? pinned : hover) as number)}
            x2={scale.x((touch ? pinned : hover) as number)}
            y1={plot.padTop}
            y2={plot.padTop + scale.innerH}
          />
        ) : null}
        {data.series.map((series, seriesIndex) => (
          <path key={series.code} className="db-line" d={pathFor(seriesIndex)} stroke={seriesColor(seriesIndex)} />
        ))}
        {(touch ? pinned : hover) !== null
          ? data.series.map((series, seriesIndex) => (
              <circle
                key={series.code}
                className="db-marker"
                cx={scale.x((touch ? pinned : hover) as number)}
                cy={scale.y(data.buckets[(touch ? pinned : hover) as number].values[seriesIndex])}
                r={4}
                fill={seriesColor(seriesIndex)}
              />
            ))
          : null}
        {/* The value at the bucket the chart was widened to show. On a line it
            sits above the point rather than on a cap; one series only, because
            several stacked at one x is a pile rather than a label. */}
        {valueAt >= 0 && data.series.length === 1 ? (
          <text
            className="db-bar-value"
            x={scale.x(valueAt)}
            y={scale.y(data.buckets[valueAt].values[0]) - 8}
            textAnchor="middle"
          >
            {format(data.buckets[valueAt].values[0])}
          </text>
        ) : null}
        {labelAt.map((index) => (
          <text
            key={data.buckets[index].key}
            className={index === highlightIndex || index === pinnedAt ? 'db-tick on' : 'db-tick'}
            x={scale.x(index)}
            y={plot.padTop + scale.innerH + (axis.angle === 0 ? 16 : 12)}
            textAnchor={axis.angle === 0 ? 'middle' : 'end'}
            transform={
              axis.angle === 0
                ? undefined
                : `rotate(${axis.angle} ${scale.x(index)} ${plot.padTop + scale.innerH + 12})`
            }
          >
            {data.buckets[index].label}
          </text>
        ))}
      </svg>
      <FocusReadout data={data} index={touch && pinned !== null ? pinned : highlightIndex} note={focusNote} />
      {touch && pinned === null && highlightIndex < 0 ? <TapHint line /> : null}
      <Legend data={data} />
      {touch ? null : node}
    </div>
  );
}

/**
 * The strip under a KPI's headline figure.
 *
 * A KPI card has one number on it, and one number cannot say whether it is the
 * shape of a normal week — so the window's buckets go under it as a sparkline.
 * Drawn from the same `ChartData` the charts use, summed across series, because
 * a KPI over several subcategories shows their total and the strip must agree
 * with the number above it.
 */
export function KpiSpark({ data }: { data: ChartData }) {
  const values = data.buckets.map((bucket) => bucket.values.reduce((running, value) => running + value, 0));
  if (values.length < 2 || values.every((value) => value === 0)) return null;
  const plot = { width: 120, height: 26, padTop: 3, padRight: 1, padBottom: 3, padLeft: 1 };
  const scale = scaler(plot, values.length, Math.max(...values, 1));
  const path = values.map((value, index) => `${index === 0 ? 'M' : 'L'}${scale.x(index)} ${scale.y(value)}`).join(' ');
  return (
    <svg className="db-spark" viewBox={`0 0 ${plot.width} ${plot.height}`} height={26} aria-hidden="true">
      <path d={path} className="db-line" stroke="var(--viz-1)" />
    </svg>
  );
}
